import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../game/config';
import { deckService } from '../services/DeckService';
import { runState } from '../services/RunState';
import { profileStore } from '../services/ProfileStore';
import { gameBridge } from '../services/GameBridge';
import { progressionService } from '../services/ProgressionService';
import { SUBJECT_THEMES, WORLD_TILES, getCharacterOption } from '../config/tileIndex';
import { getPropAnimationFromFrame } from '../config/oryxThemes';
import {
  getPaletteForSubject,
  pickFloorFrame,
  pickWallFrame,
  shouldPlaceDecal,
  pickDecalFrame,
  addHalo,
  type TilePalette,
} from '../services/TilePalette';
import type { MCQuestion } from '../types/deck';

interface EncounterSceneData {
  cardId: string;
  subject?: string;
  isRematch?: boolean;
}

const SCALE = 3;
const TILE_SIZE = 16;
const SCALED_TILE = TILE_SIZE * SCALE;

/**
 * Encounter Scene - Manages MCQ quiz logic with themed arena visuals
 * UI is rendered by React (EncounterUI.tsx), this scene handles:
 * - Card/MCQ data loading
 * - Answer validation and scoring
 * - Scene transitions
 * - Arena background rendering with subject theming
 * - Monster sprite with animations
 */
export class EncounterScene extends Phaser.Scene {
  private question!: MCQuestion;
  private cardId!: string;
  private subject!: string;
  private isRematch: boolean = false;
  private answerSubscription?: () => void;
  private returnSubscription?: () => void;
  private lastPlayerAnswer: string = '';
  private lastCorrectAnswer: string = '';

  // Visual elements
  private monster!: Phaser.GameObjects.Sprite;
  private torches: Phaser.GameObjects.Sprite[] = [];
  private player!: Phaser.GameObjects.Sprite;
  private palette!: TilePalette;

  constructor() {
    super({ key: 'EncounterScene' });
  }

  init(data: EncounterSceneData): void {
    this.cardId = data.cardId;
    this.subject = data.subject || '';
    this.isRematch = data.isRematch || false;
  }

  create(): void {
    // Get the card and generate MCQ
    const card = deckService.getCardById(this.cardId);
    if (!card) {
      console.error(`Card not found: ${this.cardId}`);
      this.returnToHub();
      return;
    }

    this.question = deckService.generateQuestion(card);
    this.lastPlayerAnswer = '';
    this.lastCorrectAnswer = '';

    // Render themed arena background
    this.createArena();

    // Create monster for this subject
    this.createMonster();

    // Create player character
    this.createPlayer();

    // Add atmospheric effects
    this.addAtmosphere();

    // Emit scene change event
    gameBridge.emit('scene-changed', { scene: 'encounter' });

    // Emit encounter started event with all data React needs
    gameBridge.emit('encounter-started', {
      cardId: this.cardId,
      card: card,
      question: this.question.question,
      options: this.question.options,
      subject: this.subject,
      isRematch: this.isRematch,
      mode: this.question.mode,
      blanks: this.question.blanks,
      hint: this.question.hint,
      explain: this.question.explain,
      scenario: this.question.scenario,
      remediation: this.question.remediation,
    });

    // Subscribe to React events
    this.setupEventListeners();
  }

  /**
   * Create themed arena floor based on subject
   */
  private createArena(): void {
    this.palette = getPaletteForSubject(this.subject);
    const theme = SUBJECT_THEMES[this.subject];

    // Calculate arena dimensions (fills most of the screen)
    const arenaWidth = 13;  // tiles
    const arenaHeight = 9;  // tiles
    const offsetX = (GAME_WIDTH - arenaWidth * SCALED_TILE) / 2;
    const offsetY = (GAME_HEIGHT - arenaHeight * SCALED_TILE) / 2 - 20; // Slightly higher for UI space

    // Draw floor tiles with edge-aware picking and decals
    for (let y = 1; y < arenaHeight - 1; y++) {
      for (let x = 1; x < arenaWidth - 1; x++) {
        const floorFrame = pickFloorFrame(x, y, arenaWidth, arenaHeight, this.palette);
        const sprite = this.add.sprite(
          offsetX + x * SCALED_TILE + SCALED_TILE / 2,
          offsetY + y * SCALED_TILE + SCALED_TILE / 2,
          'world',
          floorFrame
        );
        sprite.setScale(SCALE);

        // Occasional decals for atmosphere
        if (shouldPlaceDecal(this.palette)) {
          const decal = this.add.sprite(
            sprite.x,
            sprite.y,
            'world',
            pickDecalFrame(this.palette)
          );
          decal.setScale(SCALE);
          decal.setDepth(1);
          decal.setAlpha(0.7);
        }
      }
    }

    // Draw walls - top and bottom (horizontal) with crack variants
    for (let x = 0; x < arenaWidth; x++) {
      // Top wall
      const topWall = this.add.sprite(
        offsetX + x * SCALED_TILE + SCALED_TILE / 2,
        offsetY + SCALED_TILE / 2,
        'world',
        pickWallFrame(true, this.palette)
      );
      topWall.setScale(SCALE);

      // Bottom wall
      const bottomWall = this.add.sprite(
        offsetX + x * SCALED_TILE + SCALED_TILE / 2,
        offsetY + (arenaHeight - 1) * SCALED_TILE + SCALED_TILE / 2,
        'world',
        pickWallFrame(true, this.palette)
      );
      bottomWall.setScale(SCALE);
    }

    // Draw walls - left and right (vertical) with crack variants
    for (let y = 1; y < arenaHeight - 1; y++) {
      // Left wall
      const leftWall = this.add.sprite(
        offsetX + SCALED_TILE / 2,
        offsetY + y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        pickWallFrame(false, this.palette)
      );
      leftWall.setScale(SCALE);

      // Right wall
      const rightWall = this.add.sprite(
        offsetX + (arenaWidth - 1) * SCALED_TILE + SCALED_TILE / 2,
        offsetY + y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        pickWallFrame(false, this.palette)
      );
      rightWall.setScale(SCALE);
    }

    // Add decorative props from theme
    const props = theme?.props || [WORLD_TILES.TORCH_1];
    const propPositions = [
      { x: 2, y: 2 },
      { x: arenaWidth - 3, y: 2 },
      { x: 2, y: arenaHeight - 3 },
      { x: arenaWidth - 3, y: arenaHeight - 3 },
    ];

    for (let i = 0; i < propPositions.length; i++) {
      const pos = propPositions[i];
      const propTile = props[i % props.length];

      const prop = this.add.sprite(
        offsetX + pos.x * SCALED_TILE + SCALED_TILE / 2,
        offsetY + pos.y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        propTile
      );
      prop.setScale(SCALE);

      const animationKey = getPropAnimationFromFrame(propTile);
      if (animationKey) {
        prop.play(animationKey);
        this.torches.push(prop);
        // Add halo light effect
        addHalo(this, prop.x, prop.y, this.palette.haloColor ?? 0xffdd88, SCALED_TILE * 0.9, 0.2);
      }
    }

    // Apply ambient color tint to the scene
    if (this.palette.ambientColor) {
      const overlay = this.add.rectangle(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        GAME_WIDTH,
        GAME_HEIGHT,
        this.palette.ambientColor,
        0.12
      );
      overlay.setDepth(2).setScrollFactor(0);
    }
  }

  /**
   * Create monster sprite for the encounter
   */
  private createMonster(): void {
    const theme = SUBJECT_THEMES[this.subject];
    const monsterFrame = theme?.monster || 174; // Default to zombie

    // Position monster in upper-center of arena
    this.monster = this.add.sprite(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 3,
      'monsters',
      monsterFrame
    );
    this.monster.setScale(SCALE * 2); // Larger for visibility
    this.monster.setDepth(10);

    // Add menacing halo behind monster
    addHalo(this, GAME_WIDTH / 2, GAME_HEIGHT / 3, 0xff4444, SCALED_TILE * 1.5, 0.25);

    // Add idle bobbing animation
    this.tweens.add({
      targets: this.monster,
      y: this.monster.y - 8,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Add slight rotation wobble for menacing effect
    this.tweens.add({
      targets: this.monster,
      angle: 3,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Entrance animation
    this.monster.setAlpha(0);
    this.monster.setScale(0);
    this.tweens.add({
      targets: this.monster,
      alpha: 1,
      scaleX: SCALE * 2,
      scaleY: SCALE * 2,
      duration: 500,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Create player character in lower part of arena
   */
  private createPlayer(): void {
    const charOption = getCharacterOption(profileStore.getSelectedCharacter());
    const isHighResAtlas = charOption.texture.startsWith('k1_kim');

    if (isHighResAtlas) {
      // Kim uses high-res atlas (192x272 frames)
      const firstFrame = `${charOption.texture}_0001`;
      this.player = this.add.sprite(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 150,
        charOption.texture,
        firstFrame
      );
      // Scale appropriately for encounter display (larger than world view)
      this.player.setScale(0.5);
    } else {
      this.player = this.add.sprite(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 150,
        charOption.texture || 'monsters',
        charOption.frame
      );
      this.player.setScale(SCALE * 1.5);
    }
    this.player.setDepth(10);

    // Try to play idle animation
    const idleAnim = `${charOption.id}-idle`;
    if (this.anims.exists(idleAnim)) {
      this.player.play(idleAnim);
    }

    // Entrance animation - slide in from bottom
    this.player.y = GAME_HEIGHT + 50;
    this.tweens.add({
      targets: this.player,
      y: GAME_HEIGHT - 150,
      duration: 400,
      ease: 'Power2',
    });
  }

  /**
   * Add atmospheric effects
   */
  private addAtmosphere(): void {
    // Add particle effects for ambiance (dust motes, sparks, etc.)
    // Using simple sprites as particles for performance

    // Add some floating dust particles
    for (let i = 0; i < 5; i++) {
      const particle = this.add.circle(
        Phaser.Math.Between(100, GAME_WIDTH - 100),
        Phaser.Math.Between(100, GAME_HEIGHT - 100),
        2,
        0xffffff,
        0.3
      );

      this.tweens.add({
        targets: particle,
        y: particle.y - 50,
        alpha: 0,
        duration: Phaser.Math.Between(2000, 4000),
        repeat: -1,
        delay: Phaser.Math.Between(0, 2000),
        onRepeat: () => {
          particle.x = Phaser.Math.Between(100, GAME_WIDTH - 100);
          particle.y = Phaser.Math.Between(200, GAME_HEIGHT - 100);
          particle.alpha = 0.3;
        },
      });
    }
  }

  private setupEventListeners(): void {
    // Listen for answer selection from React UI
    this.answerSubscription = gameBridge.subscribe('answer-selected', (payload: any) => {
      if (typeof payload === 'string') {
        this.submitAnswer({ optionId: payload });
      } else {
        this.submitAnswer(payload);
      }
    });

    // Listen for return to hub request
    this.returnSubscription = gameBridge.subscribe('return-to-hub', () => {
      this.returnToHub();
    });
  }

  private submitAnswer(selection: { optionId?: string; blankSelections?: Record<string, string> }): void {
    const { mode } = this.question;
    let isCorrect = false;
    let selectedOptionId: string | undefined = selection.optionId;
    const selectedOptionIds: string[] = [];
    let blankResults: { blankId: string; selectedOptionId?: string; correctOptionId: string }[] = [];
    this.lastPlayerAnswer = '';
    this.lastCorrectAnswer = '';

    if (mode === 'cloze' && this.question.blanks?.length) {
      blankResults = this.question.blanks.map(blank => {
        const chosenId = selection.blankSelections?.[blank.id];
        if (chosenId) {
          selectedOptionIds.push(chosenId);
        }
        return {
          blankId: blank.id,
          selectedOptionId: chosenId,
          correctOptionId: blank.correctOptionId,
        };
      });

      isCorrect = blankResults.every(result => result.selectedOptionId === result.correctOptionId);

      const selectedTexts = this.question.blanks.map(blank => {
        const chosenId = selection.blankSelections?.[blank.id];
        const chosen = blank.options.find(o => o.id === chosenId);
        return chosen?.text || '';
      }).filter(Boolean);
      this.lastPlayerAnswer = selectedTexts.join(' | ');
      this.lastCorrectAnswer = this.question.blanks.map(b => b.answer).join(' | ');
    } else {
      const option = this.question.options.find(o => o.id === selectedOptionId);
      if (!option && selection.blankSelections) {
        // Fallback: if payload used blankSelections for single selection
        const firstSelection = Object.values(selection.blankSelections)[0];
        selectedOptionId = typeof firstSelection === 'string' ? firstSelection : undefined;
      }
      const resolvedOption = this.question.options.find(o => o.id === selectedOptionId);

      if (!resolvedOption) {
        console.warn('No option found for selection, ignoring answer.');
        return;
      }

      isCorrect = resolvedOption.isCorrect;
      selectedOptionIds.push(resolvedOption.id);
      this.lastPlayerAnswer = resolvedOption.text;
      const correctOption = this.question.options.find(o => o.isCorrect);
      this.lastCorrectAnswer = correctOption?.text || '';
    }

    // Record answer with RunState
    const rewards = runState.recordAnswer(isCorrect, this.cardId);

    // Award XP through progression service
    if (rewards.xp > 0) {
      progressionService.addXP(rewards.xp);
    }

    // Track achievements
    if (isCorrect) {
      progressionService.checkAchievements('correct_answers', runState.getRunData().sessionResults.filter(r => r.correct).length);
    }
    progressionService.recordStreak(rewards.streak);
    progressionService.recordSubjectStudied(this.subject);

    // Play visual feedback
    if (isCorrect) {
      this.playCorrectFeedback();
    } else {
      this.playWrongFeedback();
    }

    // Play sound effect
    if (profileStore.isAudioEnabled()) {
      if (isCorrect) {
        if (this.sound.get('correct')) {
          this.sound.play('correct', { volume: 0.5 });
        }
      } else {
        if (this.sound.get('wrong')) {
          this.sound.play('wrong', { volume: 0.5 });
        }
      }
    }

    // Emit result after feedback so UI cannot reveal early
    this.time.delayedCall(700, () => {
      gameBridge.emit('answer-submitted', {
        cardId: this.cardId,
        selectedOptionId,
        selectedOptionIds,
        correct: isCorrect,
        xp: rewards.xp,
        coins: rewards.coins,
        streak: rewards.streak,
        mode,
        blankResults,
      });
    });

    // Handle result after delay
    this.time.delayedCall(1500, () => {
      // After a delay, continue or go to judgment
      this.continueRun(isCorrect);
    });
  }

  /**
   * Play visual feedback for correct answer
   */
  private playCorrectFeedback(): void {
    // Flash monster white and shrink (defeated)
    this.tweens.add({
      targets: this.monster,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
    });

    // Player victory pose (scale up slightly)
    this.tweens.add({
      targets: this.player,
      scaleX: SCALE * 1.8,
      scaleY: SCALE * 1.8,
      duration: 300,
      yoyo: true,
    });

    // Add sparkle effects
    for (let i = 0; i < 8; i++) {
      const spark = this.add.circle(
        this.monster.x + Phaser.Math.Between(-50, 50),
        this.monster.y + Phaser.Math.Between(-50, 50),
        4,
        0xffff00
      );
      spark.setDepth(20);

      this.tweens.add({
        targets: spark,
        x: spark.x + Phaser.Math.Between(-80, 80),
        y: spark.y + Phaser.Math.Between(-80, 80),
        alpha: 0,
        scale: 0,
        duration: 600,
        ease: 'Power2',
        onComplete: () => spark.destroy(),
      });
    }
  }

  /**
   * Play visual feedback for wrong answer
   */
  private playWrongFeedback(): void {
    // Monster attack animation (lunge forward)
    this.tweens.add({
      targets: this.monster,
      y: this.monster.y + 30,
      scaleX: SCALE * 2.3,
      scaleY: SCALE * 2.3,
      duration: 200,
      yoyo: true,
      ease: 'Power2',
    });

    // Player recoil (knocked back)
    this.tweens.add({
      targets: this.player,
      y: this.player.y + 20,
      alpha: 0.5,
      duration: 300,
      yoyo: true,
    });

    // Screen shake
    this.cameras.main.shake(200, 0.01);

    // Red flash overlay
    const flash = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0xff0000,
      0.3
    );
    flash.setDepth(100);

    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 400,
      onComplete: () => flash.destroy(),
    });
  }

  private goToJudgment(playerAnswer: string): void {
    const card = deckService.getCardById(this.cardId);
    if (!card) {
      this.continueRun(false); // Pass false since we can't judge
      return;
    }

    const correctOption = this.question.options.find(o => o.isCorrect);
    const correctAnswer = this.lastCorrectAnswer || correctOption?.text || '';

    // Clean up subscriptions before leaving
    this.cleanup();

    this.scene.start('JudgmentScene', {
      cardId: this.cardId,
      subject: this.subject,
      card,
      playerAnswer,
      correctAnswer,
    });
  }

  private continueRun(wasCorrect: boolean): void {
    // If incorrect and we should show explanation, go to JudgmentScene instead
    if (!wasCorrect && profileStore.isShowExplanation()) {
      this.goToJudgment(this.lastPlayerAnswer || '');
      return;
    }

    // Return to dungeon after encounter
    this.cleanup();
    // IMPORTANT: Wake DungeonScene BEFORE stopping EncounterScene
    // Use wake() not resume() because DungeonScene was put to sleep with sleep()
    // wake() triggers the 'wake' event, resume() triggers 'resume' event
    this.scene.wake('DungeonScene', { wasCorrect });
    this.scene.stop('EncounterScene');
  }

  private returnToHub(): void {
    this.cleanup();
    runState.endRun();
    gameBridge.emit('scene-changed', { scene: 'hub' });
    this.scene.start('WorldScene');
  }

  private cleanup(): void {
    if (this.answerSubscription) {
      this.answerSubscription();
      this.answerSubscription = undefined;
    }
    if (this.returnSubscription) {
      this.returnSubscription();
      this.returnSubscription = undefined;
    }
  }

  shutdown(): void {
    this.cleanup();
  }
}
